import { useMemo, useState, useEffect, useCallback, memo } from 'react';

import BellIcon from '@heroicons/react/20/solid/BellIcon';
import MoonIcon from '@heroicons/react/20/solid/MoonIcon';
import SunIcon from '@heroicons/react/20/solid/SunIcon';
import MagnifyingGlassIcon from '@heroicons/react/20/solid/MagnifyingGlassIcon';
import { MobileNavbar } from './MobileNavbar';
import { getNavbarSizeClasses } from './constants';
import { ProfileDropdown } from './ProfileDropdown';
import classNames from 'classnames';
import { NavLink } from './NavLink';
import { IconButton } from '../_misc/buttons/IconButton';

import { LogoStyle, LogoTheme, NavbarStyle, Size } from '../../constants';
import { Logo } from './Logo';
import { NavbarThemeProps } from '../theme/constants';
import { NavbarLinks } from './NavbarLinks';
import { NotificationDropdown } from '../_misc/dropdown/NotificationDropdown';
import { NotificationPanel } from './NotificationPanel';
import useSWR from 'swr';
import { Notification } from 'referral-types';
import { baseEndpoint as cacheKey } from '../../api/NotificationApi';
import { notificationApi, pageApi } from '../../api/service';
import { useAuth } from '../../context/auth/useAuth';
import { useTheme } from '../../context/theme/useTheme';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, m } from 'framer-motion';
import { ThemeBackground } from '../../context/theme/ThemeConstantsUpdated';
import { getUserHomePageUrl } from '../user/user.constants';

const { mainBackground, borderBackground, shadow } = ThemeBackground;

const NotificationSkeleton = () => {
  return (
    <div className="animate-pulse px-3 py-2">
      <p className={classNames('bg-gray-600', 'my-2 h-2.5 w-2/3 rounded-full')} />
      <p className={classNames('bg-gray-600', 'my-2 h-2.5 w-4/5 rounded-full')} />
      <p className={classNames('bg-gray-600', 'my-2 h-2.5 w-3/5 rounded-full')} />
      <p className={classNames('bg-gray-600', 'my-2 h-2.5 w-1/3 rounded-full')} />
    </div>
  );
};

export const Navbar = memo((props: NavbarThemeProps) => {
  const {
    themeRevision,
    removeBorder,
    renderCartToggle,
    isLightingLocked,
    needsLowerZ,
    isVibrant,
    buttonType,
    backgroundClassOverride,
    logoOverride,
    pageId,
    isEditing,
    isPreviewing,
    isNavbarButtonsDisabled,
    hasSubNav,
    removeShadow,
    forceBorder,
  } = props;
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isSignedIn, currentUser } = useAuth();
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();

  const { data: notifications } = useSWR<Array<Notification>>(
    currentUser ? [cacheKey, currentUser.id, 'unread'] : undefined,
    () => notificationApi.getUnreadNotificationsForUser(),
    { suspense: false }
  );

  const navbarSize: Size = themeRevision?.navbarSize || Size.md;
  const logoTheme = isVibrant ? (isDarkMode ? LogoTheme.secondary : LogoTheme.light) : LogoTheme.primary;
  const isMinimalist = themeRevision?.navbarStyle === NavbarStyle.minimalist;
  const navbarSizeClasses = getNavbarSizeClasses(navbarSize);
  const minimalistHoverClasses = 'opacity-0 group-hover:opacity-100 delay-500 transition-opacity duration-500';

  const [hasBeenRead, setHasBeenRead] = useState<boolean>();

  const handleSearchClick = useCallback(() => {
    navigate('/search');
  }, [navigate]);

  const [overrideLogoSrc, setOverrideLogoSrc] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (logoOverride) {
      const url = URL.createObjectURL(logoOverride);

      setOverrideLogoSrc(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (pageId != null && themeRevision?.logoStyle === LogoStyle.custom) {
      setOverrideLogoSrc(pageApi.getLogoImageSrc(pageId));
    } else {
      setOverrideLogoSrc(undefined);
    }
  }, [logoOverride, themeRevision?.logoStyle, pageId]);

  const markAsRead = () => {
    if (!hasBeenRead && notifications && notifications?.length > 0) {
      notificationApi
        .markAllAsRead()
        .then(() => setHasBeenRead(true))
        .catch(() => {});
    }
  };

  const CartToggle = useMemo(() => renderCartToggle?.(buttonType), [renderCartToggle, buttonType]);

  const NavbarLogo = useMemo(() => {
    const url = currentUser ? getUserHomePageUrl(currentUser) : '/';
    if (overrideLogoSrc && themeRevision?.logoStyle !== LogoStyle.default) {
      return (
        <Link className="h-full" to={url}>
          <img src={overrideLogoSrc} alt="Logo" className={classNames('h-full w-auto object-contain py-3')} />
        </Link>
      );
    } else {
      return (
        <Logo
          className={navbarSize === Size.lg ? 'scale-110' : ''}
          size={navbarSize === Size.sm ? Size.sm : Size.md}
          options={{ excludeLink: isNavbarButtonsDisabled, customLink: url }}
          theme={logoTheme}
        />
      );
    }
  }, [overrideLogoSrc, themeRevision?.logoStyle, navbarSize, logoTheme, isNavbarButtonsDisabled]);

  return (
    <>
      <AnimatePresence key={`navbar`}>
        {(navbarSize !== Size.none || isEditing) && (
          <m.header
            key="motion_navbar_header"
            initial={{ y: '-100%' }}
            animate={{ y: '0%' }}
            exit={{ y: '-100%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className={classNames(
              backgroundClassOverride || mainBackground,
              // !removeBorder && (navbarSize !== Size.none || (isEditing && !isPreviewing)) && ['border-b', borderBackground],
              forceBorder || (!removeBorder && isEditing && !isPreviewing && ['border-b', borderBackground]),
              navbarSizeClasses.navbarHeight,
              'group fixed left-0 top-0 w-screen',
              needsLowerZ ? 'z-20' : 'z-[53]',
              !hasSubNav && !removeShadow && shadow
            )}
          >
            <nav
              className="mx-auto flex h-full max-w-7xl items-center justify-between px-4 sm:px-8"
              aria-label="Global"
            >
              <div className={classNames('flex flex-1', isMinimalist && minimalistHoverClasses)}>
                <NavbarLinks
                  setMobileMenuOpen={setMobileMenuOpen}
                  isVibrant={isVibrant}
                  isNavbarButtonsDisabled={isNavbarButtonsDisabled}
                />
              </div>
              <div className="flex h-full items-center justify-center overflow-hidden">{NavbarLogo}</div>
              <div
                className={classNames(
                  'flex flex-1 items-center justify-end gap-1.5 sm:gap-2.5',
                  isMinimalist && minimalistHoverClasses
                )}
              >
                {isSignedIn ? (
                  <>
                    <div className="hidden sm:inline-block">
                      <div className="relative flex justify-center align-middle">
                        <IconButton
                          Icon={MagnifyingGlassIcon}
                          onClick={handleSearchClick}
                          buttonType={buttonType}
                          options={{ size: Size.lg, includeAnimation: true }}
                        />
                      </div>
                    </div>

                    {!isLightingLocked && (
                      <div className="hidden sm:inline-block">
                        <div className="relative flex justify-center align-middle">
                          <IconButton
                            Icon={isDarkMode ? MoonIcon : SunIcon}
                            onClick={toggleDarkMode!}
                            buttonType={buttonType}
                            options={{ size: Size.lg, includeAnimation: true }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="hidden sm:inline-block">
                      <NotificationDropdown
                        notificationAmount={hasBeenRead ? 0 : notifications?.length || 0}
                        Icon={BellIcon}
                        buttonType={buttonType}
                        title="Notifications"
                        onClick={() => markAsRead()}
                      >
                        {notifications ? (
                          <NotificationPanel notifications={notifications} isPopup />
                        ) : (
                          <NotificationSkeleton />
                        )}
                      </NotificationDropdown>
                    </div>

                    {CartToggle}

                    <ProfileDropdown isVibrant={isVibrant} />
                  </>
                ) : (
                  <>
                    <div className="hidden sm:inline-block">
                      <IconButton
                        Icon={MagnifyingGlassIcon}
                        onClick={handleSearchClick}
                        buttonType={buttonType}
                        options={{ size: Size.lg, includeAnimation: true }}
                      />
                    </div>
                    {!isLightingLocked && (
                      <div className="mr-1 hidden sm:inline-block">
                        <IconButton
                          Icon={isDarkMode ? MoonIcon : SunIcon}
                          onClick={toggleDarkMode!}
                          buttonType={buttonType}
                          options={{ size: Size.lg, includeAnimation: true }}
                        />
                      </div>
                    )}
                    <div className="inline-block pb-2 sm:hidden">
                      <NavLink isVibrant={isVibrant} to="/login">
                        Login
                      </NavLink>
                    </div>
                    <div className="hidden pb-2 sm:inline-block">
                      <NavLink isVibrant={isVibrant} to="/login">
                        Login or Signup &rarr;
                      </NavLink>
                    </div>
                  </>
                )}
              </div>
            </nav>
          </m.header>
        )}
      </AnimatePresence>

      <MobileNavbar
        isOpen={isMobileMenuOpen}
        toggle={() => setMobileMenuOpen((isOpen) => !isOpen)}
        navbarSizeClasses={navbarSizeClasses}
        renderLightingToggle={() => (
          <>
            {!isLightingLocked && (
              <IconButton
                Icon={isDarkMode ? MoonIcon : SunIcon}
                onClick={toggleDarkMode!}
                buttonType={buttonType}
                options={{ size: Size.lg, includeAnimation: true }}
              />
            )}
          </>
        )}
      />
    </>
  );
});
